import React from 'react';

import cc from 'classcat';
import { Intent } from '@blueprintjs/core';
import {
  IconDefinition,
  faBan,
  faBox,
  faCircle,
  faCircleNotch,
  faClock,
  faCommentSlash,
  faDotCircle,
  faFlagCheckered,
  faMoon,
  faPause,
  faTrash
} from '@fortawesome/pro-solid-svg-icons';

import { TriageCategory } from '../../../contexts/triage/types';
import Icon from '../../atoms/icon';
import Tag from '../../atoms/tag';
import Tooltip from '../tooltip';

interface TriageIndicatorProps {
  color?: string;
  description: string;
  indicator: IconDefinition | string;
  intent?: Intent;
  wcag: string;
}
// TODO: Move this config into TriageCategory
const TRIAGE_INDICATORS: Record<TriageCategory, TriageIndicatorProps> = {
  0: {
    indicator: faCircle,
    description: 'No category',
    intent: 'none',
    wcag: 'No category'
  },
  1: {
    indicator: 'H',
    description: 'High-Risk',
    intent: 'danger',
    wcag: 'High-Risk'
  },
  2: {
    indicator: 'M',
    description: 'Medium-Risk',
    intent: 'warning',
    wcag: 'Medium-Risk'
  },
  3: {
    indicator: 'L',
    description: 'Low-Risk',
    intent: 'success',
    wcag: 'Low-Risk'
  },
  4: {
    indicator: faCircleNotch,
    description: 'Not Engaged',
    wcag: 'Not Engaged'
  },
  5: {
    indicator: faDotCircle,
    description: 'No responses received',
    wcag: 'without Responses'
  },
  6: {
    indicator: faCommentSlash,
    description: 'Has not consented to getting messages',
    wcag: 'Unconsented'
  },
  7: {
    indicator: faMoon,
    description: 'Snoozed',
    color: 'brand',
    wcag: 'Snoozed'
  },
  8: {
    indicator: faFlagCheckered,
    description: "Program reached it's prescribed end date",
    color: 'grey',
    wcag: 'Program ended'
  },
  9: {
    indicator: faPause,
    description: 'Service is paused',
    color: 'brand',
    wcag: 'Paused'
  },
  10: {
    indicator: faClock,
    description: 'Recently added',
    wcag: 'Added Recently'
  },
  11: {
    indicator: faTrash,
    description: 'Program removed',
    color: 'brand',
    wcag: 'Program removed'
  },
  100: {
    indicator: faBox,
    description: 'Archived',
    color: 'brand',
    wcag: 'Archived'
  },
  101: { indicator: faBan, description: 'Opted Out', wcag: 'Opted-Out' }
};

// Helper functions
function generateTooltipIcon(
  config: TriageIndicatorProps,
  colorClasses: string[]
): JSX.Element {
  if (typeof config.indicator === 'string') {
    return (
      <Tag
        className={'font-bold text-center mr-3'}
        intent={config.intent}
        text={config.indicator}
      />
    );
  } else {
    return (
      <Icon
        className={cc([...colorClasses])}
        icon={config.indicator}
        title={config.wcag}
      />
    );
  }
}
function generateIcon(
  config: TriageIndicatorProps,
  colorClasses: string[],
  minimal: boolean,
  isPatient: boolean
): JSX.Element {
  if (typeof config.indicator === 'string') {
    return (
      <Tag
        className={'font-bold text-center mr-3'}
        intent={config.intent}
        minimal={minimal}
        text={config.indicator}
      />
    );
  } else {
    const icon = (
      <Icon
        className={cc(['mr-4', ...colorClasses])}
        icon={config.indicator}
        title={config.wcag}
      />
    );
    if (isPatient) {
      // Only Snoozed, Recently Snoozed, and Recently Resolved indicators should have this
      return <Tag className="bg-white mr-3" text="" icon={icon} />;
    }
    return icon;
  }
}

const ALT_WORDING_CATEGORIES = [
  TriageCategory.Danger,
  TriageCategory.Warning,
  TriageCategory.Success
];

interface StatusIndicatorProps {
  category?: TriageCategory;
  isAltCategoryWording?: boolean;
  className?: string;
  color?: string;
  indicator?: IconDefinition;
  isPatient?: boolean;
  minimal?: boolean;
  title?: boolean | string; // WCAG 2.0, Informative Images: https://www.w3.org/WAI/tutorials/images/informative/
  tooltip?: string | React.ReactNode;
}
const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  category,
  isAltCategoryWording,
  className,
  color = 'blue3',
  indicator,
  isPatient,
  minimal,
  title,
  tooltip
}) => {
  const baseClasses = [className, 'mr-1'];
  if (category) {
    let config = TRIAGE_INDICATORS[category];
    if (!config) {
      throw new Error(
        `Status Indicator: config for ${category} not found in TRIAGE_INDICATORS.`
      );
    }
    if (isAltCategoryWording && ALT_WORDING_CATEGORIES.includes(category)) {
      let text = '';
      if (category === TriageCategory.Danger) {
        text = 'R';
      } else if (category === TriageCategory.Warning) {
        text = 'Y';
      } else if (category === TriageCategory.Success) {
        text = 'G';
      }
      config = { ...config, indicator: text };
    }
    const tooltipIcon = generateTooltipIcon(config, baseClasses);
    const tooltipIconClasses = baseClasses;
    tooltipIconClasses.push(`text-${config.color ? config.color : color}`);
    const icon = generateIcon(config, tooltipIconClasses, minimal, isPatient);
    return (
      <Tooltip
        content={
          <span>
            {tooltipIcon}
            {config.description}
          </span>
        }
      >
        {icon}
      </Tooltip>
    );
  }
  if (title === undefined || title === null) {
    console.warn(
      'WCAG: Status Indicator icons should have a meaningful title.'
    );
  }
  const iconClasses = baseClasses;
  iconClasses.push(`text-${color}`);
  const icon = (
    <Icon
      title={title}
      icon={indicator ? indicator : faCircle}
      className={cc(iconClasses)}
    />
  );
  if (tooltip) {
    // If tooltip is a string, wrap it in span. If it's already a React element, use it directly
    const tooltipContent =
      typeof tooltip === 'string' ? <span>{tooltip}</span> : tooltip;
    return <Tooltip content={tooltipContent}>{icon}</Tooltip>;
  }
  return icon;
};

export default StatusIndicator;
